import React, { useCallback, useState } from 'react';
import { LuQrCode } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { isNativeScanAvailable, scanChargerCode } from '../../../platform/scanner.js';
import { requestCameraPermission } from '../../../platform/permissions.js';
import { useOptionalToast } from '../../../context/ToastContext.jsx';

/**
 * ScanChargerButton — visible on native only.
 *
 * On tap:
 *   1. Ensure camera permission (best-effort; the user picks up
 *      permanent denials in Settings).
 *   2. Run the native barcode scanner. A valid `GCEV…` code becomes
 *      a navigation to `/c/<code>` (the existing ChargerEntry public
 *      route). Anything else surfaces a calm toast — arbitrary URLs
 *      NEVER trigger navigation.
 *
 * The button renders nothing on web so the existing search-only
 * layout stays visually identical there.
 */
export default function ScanChargerButton() {
  const navigate = useNavigate();
  const toast = useOptionalToast();
  const [scanning, setScanning] = useState(false);

  const runScan = useCallback(async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const perm = await requestCameraPermission();
      if (perm.state === 'denied') {
        toast.showToast({
          message: 'Camera access is blocked. Enable it in Settings to scan chargers.',
          type: 'warning',
          duration: 5000,
        });
        return;
      }
      const result = await scanChargerCode();
      if (result.ok && result.code) {
        navigate(`/c/${encodeURIComponent(result.code)}`);
        return;
      }
      if (result.reason === 'cancelled') return; // silent
      if (result.reason === 'permission_denied') {
        toast.showToast({ message: 'Camera permission is required to scan.', type: 'warning' });
        return;
      }
      // Invalid payload / plugin error / MLKit module still installing.
      toast.showToast({
        message: "That code isn't a GreenCharge charger. Try again or enter the code manually.",
        type: 'info',
      });
    } catch {
      toast.showToast({ message: 'Scanner failed. Please try again.', type: 'danger' });
    } finally {
      setScanning(false);
    }
  }, [navigate, scanning, toast]);

  if (!isNativeScanAvailable()) return null;

  return (
    <button
      type="button"
      className="gc-find-scan"
      onClick={runScan}
      disabled={scanning}
      aria-label="Scan a charger QR code"
    >
      <LuQrCode aria-hidden="true" />
      <span>Scan</span>
    </button>
  );
}
